import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { useDateRanges } from '../../../shared/lib/hooks/UseDateRanges'
import { ITrademarkInfringementsFilters } from './types/TrademarkInfringementsFilters.interface'
import { RootState } from '../../../app/store/Store'
import { IOption } from '../../../shared/components/forms/search-input/BpSearchInput'
import { IPagination } from '../../../shared/types/Pagination.interface'

export type TExpandedRows = number[] | 'all'

export type TTabCount = {
  to_submit: number
  in_progress: number
  submitted: number
  accepted: number
  rejected: number
  removed: number
}

type TrademarkInfringementsState = {
  filters: ITrademarkInfringementsFilters
  pagination: IPagination
  expandedRows: TExpandedRows
  tabCount: TTabCount
  project: IOption & {
    is_demo: boolean
    has_paid_links: boolean
    has_seo_links: boolean
    has_shopping_ads: boolean
    has_trademarks: boolean
    has_active_trademarks: boolean
    has_tm_infringements: boolean
  }
}

const dateRanges = useDateRanges()

const trademarkInfringementsSlice = createSlice({
  name: 'trademarkInfringements',
  initialState: {
    filters: {
      country: '',
      dateRange: dateRanges.month,
      showWhitelisted: false,
      tab: 'to_submit',
      onlyRegistered: false,
    },
    pagination: {
      current: 1,
      pageSize: 10,
      total: 1,
    },
    expandedRows: [] as TExpandedRows,
    tabCount: {
      to_submit: 0,
      in_progress: 0,
      removed: 0,
      submitted: 0,
      accepted: 0,
      rejected: 0,
    },
    project: {
      is_demo: false,
      has_trademarks: true,
      has_active_trademarks: true,
      has_tm_infringements: true,
    },
  } as TrademarkInfringementsState,
  reducers: {
    setTrademarkInfringementsFilters: (
      state: TrademarkInfringementsState,
      action: PayloadAction<ITrademarkInfringementsFilters>,
    ) => {
      state.filters = {
        ...state.filters,
        ...action.payload,
      }
    },
    setTrademarkInfringementsOnlyRegistered: (
      state: TrademarkInfringementsState,
      action: PayloadAction<boolean>,
    ) => {
      state.filters = {
        ...state.filters,
        onlyRegistered: action.payload,
      }
    },
    setTrademarkInfringementsPagination: (
      state: TrademarkInfringementsState,
      action: PayloadAction<IPagination>,
    ) => {
      state.pagination = action.payload
    },
    setExpandedRows: (state: TrademarkInfringementsState, action: PayloadAction<TExpandedRows>) => {
      state.expandedRows = action.payload
    },
    setTabCount: (state: TrademarkInfringementsState, action: PayloadAction<TTabCount>) => {
      state.tabCount = action.payload
    },
    setTrademarkInfringementsProject: (
      state: TrademarkInfringementsState,
      action: PayloadAction<
        IOption & {
          is_demo: boolean
          has_paid_links: boolean
          has_seo_links: boolean
          has_shopping_ads: boolean
          has_trademarks: boolean
          has_active_trademarks: boolean
          has_tm_infringements: boolean
        }
      >,
    ) => {
      state.project = action.payload
    },
  },
})

export const {
  setTrademarkInfringementsFilters,
  setTrademarkInfringementsOnlyRegistered,
  setTrademarkInfringementsPagination,
  setExpandedRows,
  setTabCount,
  setTrademarkInfringementsProject,
} = trademarkInfringementsSlice.actions

export const selectTrademarkInfringementsFilters = (state: RootState) =>
  state.root.trademarkInfringements.filters
export const selectTrademarkInfringementsPagination = (state: RootState) =>
  state.root.trademarkInfringements.pagination
export const selectTrademarkInfringementsExpandedRows = (state: RootState) =>
  state.root.trademarkInfringements.expandedRows
export const selectTrademarkInfringementsExpandAllRows = (state: RootState) =>
  state.root.trademarkInfringements.expandedRows === 'all'
export const selectTrademarkTabCount = (state: RootState) =>
  state.root.trademarkInfringements.tabCount
export const selectTrademarkInfringementsProject = (state: RootState) =>
  state.root.trademarkInfringements.project

export default trademarkInfringementsSlice.reducer
